import { createClient, FunctionsFetchError, FunctionsHttpError } from '@supabase/supabase-js';
import { env } from './env.ts';

/**
 * Link de email de Supabase (recuperar contraseña o invitación) con el que se abrió la app.
 * Se lee ANTES de crear el cliente, porque el cliente procesa el link y lo borra de la URL.
 * Supabase puede mandar a la página principal (Site URL) aunque se pida /definir-clave.
 */
export const authLinkOnLoad: { kind: 'recovery' | 'invite' | 'error'; message?: string } | null = (() => {
  if (typeof location === 'undefined') return null;
  const params = new URLSearchParams(location.hash.replace(/^#/, ''));
  if (params.get('error_code') || params.get('error')) return { kind: 'error', message: params.get('error_description') ?? undefined };
  const type = params.get('type');
  return type === 'recovery' || type === 'invite' ? { kind: type } : null;
})();

// Con configuración vacía se crea igual (con una URL inválida) para que la app muestre
// la pantalla de "falta configurar" en lugar de romperse al importar.
export const supabase = createClient(env.supabaseUrl || 'http://localhost', env.supabaseKey || 'missing', {
  auth: { persistSession: true, autoRefreshToken: true, storageKey: 'pos-saas-auth' },
  // En celulares un pedido puede quedar colgado para siempre (la app pasó a segundo plano o
  // cambió la red): se corta al minuto para que la pantalla no quede esperando.
  global: { fetch: (input, init) => fetch(input, { ...init, signal: init?.signal ?? AbortSignal.timeout(60_000) }) },
});

/** Sin conexión con el servidor (red caída o servidor inaccesible). */
export class OfflineError extends Error {
  constructor(message = 'No hay conexión con el servidor.') {
    super(message);
    this.name = 'OfflineError';
  }
}

/** Error de negocio devuelto por una Edge Function (mensaje listo para mostrar). */
export class FunctionError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'FunctionError';
  }
}

export function isNetworkError(error: unknown): boolean {
  if (error instanceof OfflineError || error instanceof FunctionsFetchError) return true;
  if (error instanceof Error && error.name === 'AuthRetryableFetchError') return true;
  const message = error instanceof Error ? error.message : String(error);
  return /Failed to fetch|NetworkError|Load failed|fetch failed|ERR_INTERNET/i.test(message);
}

/** Llama a una Edge Function y traduce los errores a OfflineError / FunctionError. */
export async function callFunction<T>(name: string, body: Record<string, unknown>): Promise<T> {
  if (!navigator.onLine) throw new OfflineError();
  const { data, error } = await supabase.functions.invoke<T>(name, { body });
  if (!error) return data as T;
  console.warn(`[función ${name}] falló:`, error.name, error.message, (error as { context?: unknown }).context);
  if (error instanceof FunctionsHttpError) {
    const response = error.context as Response;
    let payload: { error?: string; code?: string } = {};
    try {
      payload = await response.json();
    } catch {
      // respuesta sin JSON
    }
    throw new FunctionError(payload.error ?? 'Ocurrió un error en el servidor.', response.status, payload.code);
  }
  if (isNetworkError(error)) throw new OfflineError();
  throw error;
}
